import axios from "axios";
import { getAccessToken, refreshAccessToken, setAccessToken } from "./apiV1";

// Base URL — points to your Node.js backend
// In development: proxy in package.json handles this (set to http://localhost:3000)
// In production: set REACT_APP_API_URL in your .env
const BASE_URL = process.env.REACT_APP_API_URL || "";

const api = axios.create({
  baseURL: BASE_URL,
  // Generous: the first analytics request after a restart triggers an
  // on-demand Google/Meta sync that can take ~30-40s.
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
});

// The ad-analytics endpoints now require auth — attach the shared access
// token and transparently refresh it on a 401 (same session as /api/v1).
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        const token = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch (e) {
        setAccessToken(null);
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ─── Accounts ─────────────────────────────────────────────────

export const getAccounts = () =>
  api.get("/api/accounts").then((r) => r.data);

// ─── Campaigns ────────────────────────────────────────────────

export const getCampaigns = (platform = "", dateRange = "LAST_30_DAYS", accountId = "") =>
  api.get("/api/campaigns", { params: { platform, dateRange, accountId } }).then((r) => r.data);

export const getCampaignById = (id) =>
  api.get(`/api/campaigns/${id}`).then((r) => r.data);

export const syncCampaigns = (dateRange = "LAST_30_DAYS") =>
  api.post("/api/campaigns/sync", { dateRange }).then((r) => r.data);

// ─── Metrics ──────────────────────────────────────────────────

export const getMetricsSummary = (accountId = "", platform = "") =>
  api.get("/api/metrics/summary", { params: { accountId, platform } }).then((r) => r.data);

export const getTopPerformers = (limit = 5) =>
  api.get("/api/metrics/top-performers", { params: { limit } }).then((r) => r.data);

export const getUnderperformers = (roasThreshold = 2.0) =>
  api.get("/api/metrics/underperformers", { params: { roas_threshold: roasThreshold } }).then((r) => r.data);

// ─── Claude AI Insights ───────────────────────────────────────

export const askClaude = (question) =>
  api.post("/api/insights/ask", { question }).then((r) => r.data);

export const generateReport = (period = "last 30 days") =>
  api.post("/api/insights/report", { period }).then((r) => r.data);

export const optimizeCampaign = (campaignId) =>
  api.post(`/api/insights/optimize/${campaignId}`).then((r) => r.data);

export const comparePlatforms = (totalBudget) =>
  api.post("/api/insights/compare-platforms", { totalBudget }).then((r) => r.data);
